import { ChangeDetectorRef, Component, Input } from "@angular/core";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { CalendarDirection } from "@app/enum/CalendarDirection.enum";
import { DateRange } from "@app/enum/DateRange.enum";
import { SortDirection } from "@app/enum/SortDirection";
import { ILogRequestUpdateMessage } from "@app/main/modules/foundation/_routes/developerConsole/log-message.model";
import { CameraLogService } from "@app/main/modules/foundation/_routes/hardware/peripherals/camera-logs/camera-logs.service";
import { CommServerRequestState } from "@app/main/modules/foundation/_routes/hardware/_enums/CommServerRequestState";
import { ICameraLogFile, ILogRequest } from "@app/main/modules/foundation/_routes/hardware/_models/ILogRequest";
import { DateService, DateUnit } from "@app/services/date.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { forkJoin, Subject } from "rxjs";
import { map, tap } from "rxjs/operators";
import './camera-logs.component.scss';

@UntilDestroy()
@Component({
    selector: 'camera-logs',
    template: require('./camera-logs.component.html')
})
export class CameraLogsComponent {
    @Input() serial: string;

    logRequests: ILogRequest[];
    logFiles: ICameraLogFile[] = [];
    tableColumns: IVTableColumn<ILogRequest>[];
    tableRows: IVTableRow[];

    requestStartDate: Date = new Date();
    defaultDateRange: DateRange = DateRange.Latest4Weeks;
    loading: boolean;

    requestHour: number = 0;
    requestMinute: number = 0;
    requestSecond: number = 0;
    requestDuration: number = 10;

    private endSubscription$: Subject<void> = new Subject<void>();

    showDetailsSidebar: boolean;
    showCreateRequestSidebar: boolean;

    fileContentDictionary: { [logRequestId: number]: ICameraLogFile[] } = {};
    logRequestIdKey: string;

    CalendarDirection = CalendarDirection;

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly logService: CameraLogService,
        private readonly vTableService: VTableService,
        private readonly dateService: DateService,
        private readonly translationService: TranslationService) { }

    ngOnInit(): void {
        this.initialise();
    }

    ngOnDestroy(): void {
        this.endSubscription$.next();
        this.endSubscription$ = null;
    }

    get logRequestFileCount(): number {
        return this.logRequests.find(r => r.Id == this.logRequestIdKey).FileCount;
    }

    onRowClicked(row: IVTableRow): void {
        this.logRequestIdKey = row.data.Id
        this.showDetailsSidebar = true;
    }

    onDetailClose(): void {
        this.showDetailsSidebar = false;
    }

    // Not translated as these are proper nouns in the P100 world
    getFileTypeString(fileName: string): string {
        if (fileName.indexOf("TELEMATICS_LOGS") > -1) {
            return this.translationService.t("CameraLogs.Telematics");
        } else if (fileName.indexOf("UPDATER_LOGS") > -1) {
            return this.translationService.t("CameraLogs.Updater");
        } else if (fileName.indexOf("SDCARD_FILE_TREE") > -1) {
            return this.translationService.t("CameraLogs.SdCardTree");
        } else {
            return this.translationService.t("CameraLogs.UnknownFileType");
        }
    }

    downloadFile(cameraLogFile: ICameraLogFile): void {
        const localFile = new Blob([cameraLogFile.FileContent], { type: "text/plain" });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(localFile);
        link.download = cameraLogFile.FileName;
        link.click();
        link.remove();
    }

    // request methods
    toggleCreateRequestSidebar(): void {
        this.showCreateRequestSidebar = !this.showCreateRequestSidebar;
    }

    onDateChanged($event: Date): void {
        this.requestStartDate = $event;

        this.onSelectedTimeChanged();
    }

    onSelectedTimeChanged(): void {
        const hoursDate = new Date(this.requestStartDate).setHours(this.requestHour);
        const minuteDate = new Date(hoursDate).setMinutes(this.requestMinute);
        const secondDate = new Date(minuteDate).setSeconds(this.requestSecond);

        this.requestStartDate = new Date(secondDate);
    }

    requestLogs(): void {
        const newLogRequest: ILogRequest = {
            Id: '',
            RequestState: CommServerRequestState.Pending,
            StartTime: this.requestStartDate,
            EndTime: this.dateService.addOrSubtractTime(this.requestStartDate, this.requestDuration, DateUnit.Min),
            Serial: this.serial,
            RequestTime: new Date(),
            FileCount: 0
        }

        this.logService.createLogRequest(newLogRequest).pipe(untilDestroyed(this), tap(logRequest => {
            if (!this.logRequests) {
                this.logRequests = [logRequest];
            } else {
                this.logRequests.push(logRequest);
            }


            this.createLogRequestTableRows();
            this.toggleCreateRequestSidebar();
        })).subscribe();
    }

    //--

    private initialise(): void {
        this.createTableColumns();
        this.getLogRequests();
    }

    private getLogRequests(): void {
        this.loading = true;
        this.endSubscription$.next();

        forkJoin({
            logRequests: this.logService.getLogRequests({ Serial: this.serial }),
            logFiles: this.logService.getLogFiles({ Serial: this.serial })
        }).pipe(
            untilDestroyed(this),
            map(data => {
                this.logRequests = data.logRequests;
                this.logFiles = data.logFiles;
                this.fileContentDictionary = data.logFiles.reduce((requestId, currentFile) => {

                    if (requestId[currentFile.LogRequestId]) {
                        requestId[currentFile.LogRequestId].push(currentFile);
                    } else {
                        requestId[currentFile.LogRequestId] = [currentFile];
                    }

                    return requestId;
                }, {} as { [logRequestId: number]: ICameraLogFile[] })

                this.createLogRequestTableRows();
                this.loading = false;

            })).subscribe();
    }

    private updateRequestStatus(logRequestMessage: ILogRequestUpdateMessage): void {
        const logRequestToUpdate = this.logRequests?.find(r => r.Id == logRequestMessage.RequestId);

        if (logRequestToUpdate === undefined) {
            return;
        }

        logRequestToUpdate.RequestState = logRequestMessage.CommServerRequestState;

        if (logRequestMessage.FileName?.length) {
            this.logFiles.push({
                Id: '',
                FileName: logRequestMessage.FileName,
                FileContent: logRequestMessage.FileContent,
                LogRequestId: logRequestMessage.RequestId
            })
        }

        if (!!logRequestMessage.FileCount) {
            logRequestToUpdate.FileCount = logRequestMessage.FileCount;
        }

        if (!!logRequestMessage.SentTime) {
            logRequestToUpdate.SentTime = logRequestMessage.SentTime;
        }

        this.fileContentDictionary = this.logFiles.reduce((requestId, currentFile) => {

            if (requestId[currentFile.LogRequestId]) {
                requestId[currentFile.LogRequestId].push(currentFile);
            } else {
                requestId[currentFile.LogRequestId] = [currentFile];
            }

            return requestId;
        }, {} as { [logRequestId: number]: ICameraLogFile[] })

        this.createLogRequestTableRows();
        this.cd.detectChanges();
    }

    private createTableColumns(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createDateTableHeader({ tKey: "Common.Start", sorted: true, sortDirection: SortDirection.Descending }),
                createCellFunction: logRequest => this.vTableService.createDateCell(logRequest.StartTime)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "Common.End" }),
                createCellFunction: logRequest => this.vTableService.createDateCell(logRequest.EndTime)
            },
            {
                header: this.vTableService.createTableHeader({ title: "Log Duration" }),
                createCellFunction: logRequest => this.vTableService.createDurationCell(this.dateService.getDiff(logRequest.StartTime, logRequest.EndTime, DateUnit.Min), DateUnit.Min)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Status" }),
                createCellFunction: logRequest => this.vTableService.createEnumCell(CommServerRequestState, 'CommServerRequestState', logRequest.RequestState)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "cameraService.Requested" }),
                createCellFunction: logRequest => this.vTableService.createDateCell(logRequest.RequestTime)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "CameraLogs.Sent" }),
                createCellFunction: logRequest => this.vTableService.createDateCell(logRequest.SentTime)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CameraLogs.FileCount" }),
                createCellFunction: logRequest => this.vTableService.createTableCell(logRequest.FileCount)
            }
        ]
    }

    private createLogRequestTableRows(): void {
        this.tableRows = this.vTableService.createTableRows(this.logRequests, this.tableColumns);
    }
}
